import { errorToast } from "@/v2-ui/toast"
import {
  ButtonHint,
  ConfirmButton,
} from "@/v2-backoffice-client/components/button"
import type { ConfirmButtonProps } from "@/v2-backoffice-client/components/button"
import { useLayerEnableManyMutation } from "@/v2-backoffice-client/layer/enable/many/layer.enable.many.mutation"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"
import { LAYER_COUNTS_QUERY_NAME } from "@/v2-backoffice-client/layer/counts/layer.counts.constants"
import { IS_CATALOG_AUTHORING_ENABLED } from "@/v2-backoffice-client/feature/feature.constants"

export type LayerReviewPublishButtonProps = Omit<
  ConfirmButtonProps,
  "onConfirm"
> & {
  layers: LayerFragment[]
  onDone?(): void
}

/**
 * Takes one or more pending layers live: enables them for customers and
 * stamps the reviewer, which removes them from the review queue. The whole
 * selection goes through one batch mutation.
 *
 * Deliberately not called "Publish": the catalog publish (the page header's
 * "Publish catalog…", the Translation Publish workflow) ships definitions to
 * a runtime and never changes what customers see, while this button does
 * nothing but that. Two buttons both reading "Publish" sent an operator
 * through the wrong one (Slack, 2026-09-30).
 */
function LayerReviewPublishButton(props: LayerReviewPublishButtonProps) {
  const { layers, onDone, children, ...buttonProps } = props

  const [toggle, { loading, error }] = useLayerEnableManyMutation({
    refetchQueries: [LAYER_COUNTS_QUERY_NAME],
  })
  errorToast(error, {
    title: "Failed to take the layer live",
  })

  const count = layers.length

  return (
    <ConfirmButton
      variant="success"
      size="sm"
      iconSrc="eye"
      isLoading={loading}
      confirmContent={
        count === 1
          ? "Show this layer to all customers?"
          : `Show ${count} layers to all customers?`
      }
      confirmVariant="success"
      content={
        <ButtonHint title="Go live — show to customers in this environment">
          Makes the layer visible to every customer in the map chooser right
          away and removes it from the review queue. Nothing is copied anywhere:
          this flips the layer&apos;s enabled flag in this environment only.
          {/* Only mention Push prod where that button actually exists. */}
          {IS_CATALOG_AUTHORING_ENABLED &&
            " Production is untouched — copying the definition there is Push prod's job, and the prod review queue has its own Go live."}
        </ButtonHint>
      }
      onConfirm={async () => {
        await toggle({
          variables: {
            featureCodes: layers.map((layer) => layer.FeatureCode),
            isEnabled: true,
          },
        })
        onDone?.()
      }}
      {...buttonProps}
    >
      {children ?? "Go live"}
    </ConfirmButton>
  )
}

export default LayerReviewPublishButton
